import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  SprayCan,
  DropletOff,
  TrendingUp,
  MapPinned,
  GraduationCap,
  Briefcase,
  Users,
  BadgeCheck,
  Megaphone,
  Timer,
  Leaf,
  Ban,
  MapPin,
  Coins,
  ArrowRight,
  MessageCircle,
  Phone,
  Mail,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFab from "../components/WhatsAppFab";
import { BRAND } from "../data/brand";

const benefits = [
  { icon: SprayCan, title: "Metodo Innovativo", sub: "Ad Aria Compressa", desc: "Pulizia profonda, veloce e professionale." },
  { icon: DropletOff, title: "Zero Acqua", sub: "Senza Vincoli", desc: "Lavori ovunque, senza scarichi." },
  { icon: TrendingUp, title: "Guadagni Potenziali", sub: "Fino a €5.000 / mese", desc: "In base a operatività e crescita clienti." },
  { icon: MapPinned, title: "Esclusiva Territoriale", sub: "Proteggi la tua area", desc: "Zona riservata per il tuo business." },
  { icon: GraduationCap, title: "Formazione Completa", sub: "Tecnica, commerciale e gestionale", desc: "Ti formiamo noi, parti subito!" },
];

const includes = [
  { icon: Briefcase, label: "Kit Completo Attrezzatura Professionale" },
  { icon: SprayCan, label: "Prodotti Professionali di Alta Qualità" },
  { icon: Users, label: "Supporto Continuo" },
  { icon: BadgeCheck, label: "Metodo NexaWash Esclusivo" },
  { icon: Megaphone, label: "Materiale Marketing e Promozionale" },
];

const quickBenefits = [
  { icon: Timer, label: "Pulizia Profonda e Veloce" },
  { icon: Leaf, label: "Zero Sprechi d'Acqua" },
  { icon: Ban, label: "Nessun Problema di Scarichi" },
  { icon: MapPin, label: "Utilizzabile Ovunque" },
  { icon: Coins, label: "Costi Operativi Ridotti" },
];

export default function LavoraConNoi() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const whatsappUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    "Ciao NexaWash, vorrei informazioni sull'opportunità di diventare Partner."
  )}`;

  return (
    <div className="App grain relative">
      <Navbar />

      <main>
        {/* HERO */}
        <section
          data-testid="partner-hero"
          className="relative min-h-[92vh] flex items-center overflow-hidden pt-28 pb-20"
        >
          <div className="absolute inset-0 bg-grid opacity-40" />
          <div className="absolute inset-0 bg-radial-spot" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black pointer-events-none" />

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-px bg-nexa-blue" />
              <span className="overline">Affiliazione / Partnership</span>
            </div>

            <div className="heading text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-8 max-w-5xl">
              <span className="chrome-text">Diventa</span>{" "}
              <span className="text-nexa-blue blue-glow">Partner</span>
              <br />
              <span className="text-white">NexaWash.</span>
            </div>

            <p className="text-base md:text-xl text-nexa-muted max-w-2xl leading-relaxed mb-10">
              Apri la tua attività di car detailing{" "}
              <span className="text-white font-semibold">senza locale</span> e con un{" "}
              <span className="text-nexa-blue font-semibold">investimento ridotto</span>. Il futuro del car detailing è nelle tue mani.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="partner-cta-whatsapp"
                className="btn-primary"
              >
                Richiedi Informazioni
                <ArrowRight size={16} />
              </a>
              <a href="#dettagli" className="btn-outline">
                Scopri di più
              </a>
            </div>

            {/* Floating stat */}
            <div className="mt-16 grid sm:grid-cols-3 gap-6 max-w-3xl">
              <div className="card-surface p-6">
                <div className="overline mb-2">Investimento</div>
                <div className="heading text-3xl text-white">da <span className="text-nexa-blue">€3.500</span></div>
              </div>
              <div className="card-surface p-6">
                <div className="overline mb-2">Guadagni</div>
                <div className="heading text-3xl text-white">fino a <span className="text-nexa-blue">€5k</span>/mese</div>
              </div>
              <div className="card-surface p-6">
                <div className="overline mb-2">Locale</div>
                <div className="heading text-3xl text-white">non <span className="text-nexa-blue">necessario</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* 5 BENEFITS */}
        <section id="dettagli" className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-3xl mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-px bg-nexa-blue" />
                <span className="overline">Perché NexaWash</span>
              </div>
              <div className="heading text-3xl md:text-5xl text-white leading-tight">
                Un business <span className="text-nexa-blue">scalabile</span>, con basso rischio d'ingresso.
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} data-testid={`partner-benefit-${i}`} className="card-surface p-7">
                    <div className="w-14 h-14 flex items-center justify-center border border-nexa-blue/30 bg-nexa-blue/5 mb-5">
                      <Icon size={24} className="text-nexa-blue" />
                    </div>
                    <div className="heading text-lg uppercase tracking-wider text-white mb-1">{b.title}</div>
                    <div className="text-nexa-blue text-sm font-semibold mb-3">{b.sub}</div>
                    <p className="text-sm text-nexa-muted leading-relaxed">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INVESTMENT + INCLUDES */}
        <section className="relative py-24 md:py-32 bg-gradient-to-b from-transparent via-nexa-surface/30 to-transparent">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
            {/* Left: investment card */}
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-px bg-nexa-blue" />
                <span className="overline">Investimento</span>
              </div>
              <div className="heading text-3xl md:text-5xl text-white leading-tight mb-8">
                Da <span className="text-nexa-blue blue-glow">€3.500</span><br />
                per iniziare.
              </div>
              <div className="card-surface p-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center border border-nexa-blue text-nexa-blue text-xs shrink-0 mt-0.5">✓</div>
                    <div>
                      <div className="heading text-white uppercase tracking-wider text-sm">Nessun Locale</div>
                      <div className="text-nexa-muted text-xs mt-1">Operi direttamente dal veicolo / dai clienti</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center border border-nexa-blue text-nexa-blue text-xs shrink-0 mt-0.5">✓</div>
                    <div>
                      <div className="heading text-white uppercase tracking-wider text-sm">Nessun Affitto</div>
                      <div className="text-nexa-muted text-xs mt-1">Zero canone mensile di locazione</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center border border-nexa-blue text-nexa-blue text-xs shrink-0 mt-0.5">✓</div>
                    <div>
                      <div className="heading text-white uppercase tracking-wider text-sm">Costi di Gestione Ridotti</div>
                      <div className="text-nexa-muted text-xs mt-1">Utenze minime, prodotti ad alta resa</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: includes */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-px bg-nexa-blue" />
                <span className="overline">Cosa include</span>
              </div>
              <div className="heading text-2xl md:text-3xl text-white leading-tight mb-8">
                Il pacchetto completo per <span className="text-nexa-blue">partire subito.</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {includes.map((it, i) => {
                  const Icon = it.icon;
                  return (
                    <div key={i} data-testid={`partner-include-${i}`} className="flex items-start gap-4 p-5 border border-white/5 bg-black/40 hover:border-nexa-blue/40 transition-colors">
                      <Icon size={22} className="text-nexa-blue shrink-0 mt-0.5" />
                      <div className="heading text-white text-sm uppercase tracking-wider leading-snug">{it.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* QUICK BENEFITS STRIP */}
        <section className="relative py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5">
              {quickBenefits.map((q, i) => {
                const Icon = q.icon;
                return (
                  <div
                    key={i}
                    data-testid={`partner-quick-${i}`}
                    className="bg-nexa-bg p-6 flex flex-col items-center text-center group hover:bg-nexa-surface2 transition-colors"
                  >
                    <Icon size={28} className="text-nexa-blue mb-3" strokeWidth={1.5} />
                    <div className="heading text-xs uppercase tracking-wider text-white leading-tight">{q.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative py-24 md:py-32">
          <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-px bg-nexa-blue" />
              <span className="overline">Affiliazioni Disponibili</span>
              <div className="w-12 h-px bg-nexa-blue" />
            </div>

            <div className="heading text-3xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Il futuro del <span className="text-nexa-blue blue-glow">car detailing</span><br />
              è nelle tue mani.
            </div>

            <p className="text-lg md:text-xl text-nexa-muted mb-4 italic">
              Non è un semplice lavaggio. È un vero business.
            </p>
            <p className="text-sm text-nexa-blue uppercase tracking-widest font-semibold mb-12">
              Posti limitati per zona
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="partner-final-whatsapp"
                className="btn-primary"
              >
                <MessageCircle size={16} />
                Scrivici su WhatsApp
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                data-testid="partner-final-phone"
                className="btn-outline"
              >
                <Phone size={16} />
                {BRAND.phoneDisplay}
              </a>
              <a
                href={`mailto:${BRAND.email}?subject=${encodeURIComponent("Richiesta informazioni Partnership NexaWash")}`}
                data-testid="partner-final-email"
                className="btn-outline"
              >
                <Mail size={16} />
                Email
              </a>
            </div>

            <div className="mt-12">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-nexa-muted hover:text-nexa-blue transition-colors uppercase tracking-wider"
                data-testid="partner-back-home"
              >
                ← Torna alla home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
